import React, { useState, useEffect } from "react";
import { Button } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaFileAlt } from "react-icons/fa";

const PAST_INITIAL_COUNT = 3;

const ConcertItem = ({ concert, past, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <motion.div
      ref={ref}
      className={`concertItem ${past ? "past" : ""}`}
      initial={{ opacity: 0, x: -20 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.4, delay: Math.min(index * 0.07, 0.35) }}
    >
      <span className="dateTag">
        {concert.date}
        {concert.time ? ` | ${concert.time}` : ""}
      </span>
      <div className="concertInfo">
        <span className="concertTitle">{concert.title}</span>
        {concert.location && (
          <span className="concertLocation">{concert.location}</span>
        )}
      </div>
      {concert.pdfKey && concert.pdfKey !== "NONE" ? (
        <a
          href={`/konzert/${concert.pdfKey}`}
          target="_blank"
          rel="noopener noreferrer"
          className="pdfLink"
        >
          <FaFileAlt size={13} />
          <span className="pdfLinkText">Programm ansehen</span>
        </a>
      ) : past ? null : (
        <span
          className="pdfLink pdfLinkDisabled"
          title="Programm noch nicht verfügbar"
        >
          <FaFileAlt size={13} />
          <span className="pdfLinkText">Programm folgt</span>
        </span>
      )}
    </motion.div>
  );
};

function AppConcerts() {
  const [allConcerts, setAllConcerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllPast, setShowAllPast] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    fetch("/data/concerts.json")
      .then((r) => r.json())
      .then((data) => {
        setAllConcerts(data.concerts || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().split("T")[0];

  // Auto-sort: upcoming = dateISO >= today (aufsteigend), past = dateISO < today (absteigend)
  const upcoming = allConcerts
    .filter((c) => c.dateISO >= today)
    .sort((a, b) => a.dateISO.localeCompare(b.dateISO));

  const allPast = allConcerts
    .filter((c) => c.dateISO < today)
    .sort((a, b) => b.dateISO.localeCompare(a.dateISO));

  const visiblePast = showAllPast
    ? allPast
    : allPast.slice(0, PAST_INITIAL_COUNT);
  const hiddenCount = allPast.length - PAST_INITIAL_COUNT;

  return (
    <div id="concerts" className="concertsBlock">
      <div className="container-fluid">
        <motion.div
          className="titleHolder"
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <h2>Konzerte</h2>
        </motion.div>

        <p className="sectionIntro">
          Wir üben jährlich zwei unterschiedliche Programme ein. Diese schließen
          wir mit ca. zwei bis vier Konzerten pro Saison ab. Unsere mitreißenden
          Darbietungen begeistern immer wieder unser Publikum und hinterlassen
          bleibende Eindrücke. Wir freuen uns darauf, dich bei einem unserer
          Auftritte begrüßen zu dürfen!
        </p>

        {!loading && (
          <>
            {/* Aktuelle Konzerte */}
            <div className="concertGroup">
              <h3>Aktuelle Konzerttermine</h3>
              {upcoming.length > 0 ? (
                upcoming.map((concert, i) => (
                  <ConcertItem
                    key={concert.dateISO + i}
                    concert={concert}
                    past={false}
                    index={i}
                  />
                ))
              ) : (
                <div className="noConcerts">
                  Aktuell sind keine Konzerte geplant. Schau bald wieder vorbei
                  – wir kündigen neue Termine hier und auf Instagram an!
                </div>
              )}
            </div>

            {/* Vergangene Konzerte */}
            {allPast.length > 0 && (
              <div className="concertGroup">
                <h3>Vergangene Konzerte</h3>
                <AnimatePresence initial={false}>
                  {visiblePast.map((concert, i) => (
                    <ConcertItem
                      key={concert.dateISO + i}
                      concert={concert}
                      past={true}
                      index={i}
                    />
                  ))}
                </AnimatePresence>

                {/* Load More / Einklappen */}
                {allPast.length > PAST_INITIAL_COUNT && (
                  <motion.div
                    className="concertMoreBtn"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Button
                      type="default"
                      onClick={() => setShowAllPast(!showAllPast)}
                    >
                      {showAllPast
                        ? "Weniger anzeigen"
                        : hiddenCount === 1
                        ? "1 weiteres Konzert anzeigen"
                        : `${hiddenCount} weitere Konzerte anzeigen`}
                    </Button>
                  </motion.div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AppConcerts;
